import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import { ACTIVITY_TYPES } from '../constants/activityTypes';
import AddClubModal from './AddClubModal';
import './ClubsScreen.css';

interface Club {
  id: number;
  uuid?: string;
  name: string;
  description: string;
  website_url: string;
  activity_type: string;
  location?: string;
  county?: string;
  cost?: number;
  website_title?: string;
  website_description?: string;
  website_favicon?: string;
  contact_phone?: string;
  contact_email?: string;
  metadata_fetched_at?: string;
  created_at: string;
  usage_count?: number;
  first_used_date?: string;
  last_used_date?: string;
  min_child_age?: number;
  max_child_age?: number;
  parent_uuid?: string;
  is_private?: boolean;
  venue?: string;
  max_participants?: number;
}

interface ClubsScreenProps {
  isSelectionMode?: boolean;
  selectionFieldType?: 'location' | 'website_url';
  onClubSelect?: (clubData: any) => void;
  onCancel?: () => void;
}

const ClubsScreen: React.FC<ClubsScreenProps> = ({
  isSelectionMode: propIsSelectionMode,
  selectionFieldType: propSelectionFieldType,
  onClubSelect,
  onCancel
}) => {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');

  // Selection mode for activity creation flow
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectionFieldType, setSelectionFieldType] = useState<'location' | 'website_url' | ''>('');
  const [returnUrl, setReturnUrl] = useState<string>('');

  // Add Club Modal state
  const [showAddClubModal, setShowAddClubModal] = useState(false);
  const [editingClub, setEditingClub] = useState<Club | null>(null);
  const [currentUserUuid, setCurrentUserUuid] = useState<string>('');

  const apiService = ApiService.getInstance();

  // Fetch user profile and set default location (runs once on mount)
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await apiService.getProfile();
        if (response.success && response.data) {
          setCurrentUserUuid(response.data.uuid);

          // Set default location filter to parent's county/state if available
          if (response.data.state_province_country) {
            setLocationFilter(response.data.state_province_country);
          }
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }
    };

    fetchUserProfile();
  }, []); // Run once on mount

  // Handle selection mode and scrolling
  useEffect(() => {
    // Only scroll to top if not in modal mode
    if (!propIsSelectionMode) {
      window.scrollTo(0, 0);
    }

    // Use props if available, otherwise check URL parameters
    if (propIsSelectionMode !== undefined) {
      setIsSelectionMode(propIsSelectionMode);
      setSelectionFieldType(propSelectionFieldType || '');
    } else {
      // Check if we're in selection mode via URL
      const urlParams = new URLSearchParams(window.location.search);
      const selectParam = urlParams.get('select');
      const returnParam = urlParams.get('return');

      if (selectParam && returnParam) {
        setIsSelectionMode(true);
        setSelectionFieldType(selectParam as 'location' | 'website_url');
        setReturnUrl(decodeURIComponent(returnParam));
      }
    }
  }, [propIsSelectionMode, propSelectionFieldType]);

  // Load clubs when filters change
  useEffect(() => {
    loadClubs();
  }, [selectedType, searchTerm, locationFilter]); // Reload when filters change

  const loadClubs = async () => {
    try {
      setLoading(true);
      
      
      const response = await apiService.getClubs(selectedType, searchTerm, locationFilter);
      
      if (response.success) {
        const clubsData = response.data || [];
        setClubs(clubsData);
      } else {
        throw new Error(response.error || 'Failed to load clubs');
      }
      
    } catch (error) {
      // Fall back to empty array on error but show user-friendly message
      setClubs([]);
    } finally {
      setLoading(false);
    }
  };

  const openWebsite = (url: string) => {
    if (url && !url.startsWith('http')) {
      url = `https://${url}`;
    }
    window.open(url, '_blank');
  };

  const handleClubSelection = (club: Club) => {
    if (propIsSelectionMode || isSelectionMode) {
      // Prepare club data for return
      const clubData = {
        id: club.id,
        uuid: (club as any).uuid,
        name: club.name,
        location: club.location || '',
        website_url: club.website_url || '',
        activity_type: club.activity_type || '',
        website_title: club.website_title || '',
        website_description: club.website_description || '',
        website_favicon: club.website_favicon || '',
        contact_phone: club.contact_phone || '',
        contact_email: club.contact_email || '',
        venue: club.venue || '',
        cost: club.cost,
        max_participants: club.max_participants
      };

      // Use callback if provided (modal mode), otherwise navigate (URL mode)
      if (onClubSelect) {
        onClubSelect(clubData);
      } else if (returnUrl) {
        // Return to activity creation with club data using React Router
        const returnUrlWithData = `${returnUrl}?clubData=${encodeURIComponent(JSON.stringify(clubData))}`;
        navigate(returnUrlWithData.replace(window.location.origin, ''));
      }
    } else {
      // Normal browsing mode - open website
      openWebsite(club.website_url);
    }
  };

  const handleSaveNewClub = async (clubData: {
    name: string;
    website_url: string;
    location: string;
    venue: string;
    activity_type: string;
    cost: string;
    max_participants: string;
    contact_phone: string;
    contact_email: string;
    is_private: boolean;
  }) => {
    try {
      let response;

      if (editingClub) {
        // Update existing club
        response = await apiService.updateClub(editingClub.id, clubData);
        console.log('✅ Club updated successfully:', response.data);
      } else {
        // Create new club
        response = await apiService.createClub(clubData);
        console.log('✅ Club created successfully:', response.data);
      }

      if (response.success && response.data) {
        // Close the modal
        setShowAddClubModal(false);
        setEditingClub(null);

        // Reload clubs to show the updated/new club
        await loadClubs();

        // If in selection mode and creating a new club, automatically select it
        if (!editingClub && (propIsSelectionMode || isSelectionMode) && onClubSelect) {
          const newClub = response.data.club || response.data;
          const clubDataForSelection = {
            id: newClub.id,
            uuid: newClub.uuid,
            name: newClub.name,
            location: newClub.location || '',
            website_url: newClub.website_url || '',
            activity_type: newClub.activity_type || '',
            venue: newClub.venue || '',
            cost: newClub.cost,
            max_participants: newClub.max_participants,
            website_title: '',
            website_description: '',
            website_favicon: '',
            contact_phone: newClub.contact_phone || '',
            contact_email: newClub.contact_email || ''
          };
          onClubSelect(clubDataForSelection);
        }
      } else {
        console.error('❌ Failed to save club:', response.error);
        alert(`Failed to save club: ${response.error}`);
      }
    } catch (error) {
      console.error('❌ Error saving club:', error);
      alert('An error occurred while saving the club');
    }
  };


  return (
    <div className="clubs-screen" data-selection-mode={propIsSelectionMode || isSelectionMode}>
      <div className="clubs-header">
        {(propIsSelectionMode || isSelectionMode) ? (
          <>
            <div className="selection-header">
              <h2>Select Club for Activity</h2>
              <button
                className="close-selection-btn"
                onClick={() => {
                  // Use callback if provided (modal mode), otherwise navigate (URL mode)
                  if (onCancel) {
                    onCancel();
                  } else if (returnUrl) {
                    // Add a special parameter to indicate cancellation so the activity form can restore draft data
                    const returnUrlWithCancel = `${returnUrl}${returnUrl.includes('?') ? '&' : '?'}clubCancel=true`;
                    navigate(returnUrlWithCancel.replace(window.location.origin, ''));
                  }
                }}
                title="Close and return to activity creation"
              >
                ✕
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>Browse Clubs & Activities</h2>
            <p>Discover local clubs and activities for your children</p>
          </>
        )}
      </div>

      {(propIsSelectionMode || isSelectionMode) && (
        <div style={{
          padding: '20px 24px',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderBottom: '2px solid #dee2e6',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <button
            onClick={() => setShowAddClubModal(true)}
            style={{
              width: '100%',
              maxWidth: '600px',
              padding: '18px 32px',
              background: 'linear-gradient(135deg, #8B3FD9, #764ba2)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '18px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(139, 63, 217, 0.4)',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(139, 63, 217, 0.5)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(139, 63, 217, 0.4)';
            }}
          >
            <span style={{ fontSize: '22px' }}>➕</span>
            <span>Add New Club</span>
          </button>
        </div>
      )}

      <div className="clubs-filters">
        <div className="search-filter">
          <input
            type="text"
            placeholder="🔍 Search clubs, activities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onInput={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
            className="search-input"
          />
        </div>

        <div className="type-filter">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="type-select"
          >
            <option value="">All Activity Types</option>
            {ACTIVITY_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="location-filter">
          <input
            type="text"
            placeholder="🗺️ Filter by town/city or county (e.g., London, Kent)"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="location-input"
          />
        </div>
      </div>

      <div className="clubs-stats">
        <p>Showing {clubs.length} clubs{(searchTerm || selectedType || locationFilter) && ' matching your filters'}</p>
        {(searchTerm || selectedType || locationFilter) && (
          <p className="active-filters">
            {searchTerm && <span className="filter-tag">Search: "{searchTerm}"</span>}
            {selectedType && <span className="filter-tag">Type: {selectedType}</span>}
            {locationFilter && <span className="filter-tag">Location: "{locationFilter}"</span>}
            <button 
              onClick={() => { setSearchTerm(''); setSelectedType(''); setLocationFilter(''); }}
              className="clear-filters-btn"
            >
              Clear all filters
            </button>
          </p>
        )}
      </div>

      <div className="clubs-grid">
        {loading ? (
          <div className="no-clubs">
            <div className="no-clubs-icon">⏳</div>
            <h3>Loading clubs...</h3>
            <p>Please wait while we fetch the latest club information.</p>
          </div>
        ) : clubs.length === 0 ? (
          <div className="no-clubs">
            <div className="no-clubs-icon">🔍</div>
            <h3>No clubs found</h3>
            <p>
              {searchTerm || selectedType || locationFilter
                ? "Try adjusting your search or filter criteria"
                : "No clubs are available at the moment"
              }
            </p>
          </div>
        ) : (
          clubs
            .filter(club => {
              // In selection mode, only show clubs with the required field if that field is actually being selected
              // Note: website_url is now optional, so we don't filter it out anymore
              if (propIsSelectionMode || isSelectionMode) {
                if (selectionFieldType === 'location' && !club.location) {
                  return false;
                }
                // Removed filter for website_url since it's now optional
              }
              return true;
            })
            .map(club => (
            <div key={club.id} className="club-card">
              <div className="club-favicon">
                {club.website_favicon && club.website_favicon.trim() !== '' ? (
                  <>
                    <img
                      src={club.website_favicon}
                      alt="Website favicon"
                      className="favicon-img"
                      onError={(e) => {
                        // Fallback to globe icon if favicon fails to load
                        const fallbackElement = e.currentTarget.parentElement?.querySelector('.favicon-fallback') as HTMLElement;
                        if (fallbackElement) {
                          fallbackElement.style.display = 'inline-block';
                        }
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <div className="favicon-fallback" style={{ display: 'none' }}>🌐</div>
                  </>
                ) : (
                  <div className="favicon-fallback" style={{ display: 'inline-block' }}>🌐</div>
                )}
              </div>
              
              <div className="club-content">
                <div className="club-header">
                  <h3 className="club-title">
                    {(club.website_url && club.website_title) ? club.website_title : club.name}
                    {club.is_private && (
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '14px',
                        opacity: 0.7
                      }} title="Private club - only visible to you">
                        🔒
                      </span>
                    )}
                  </h3>
                </div>
                
                {club.website_url && (
                  <div className="club-url">
                    <a
                      href={club.website_url.startsWith('http') ? club.website_url : `https://${club.website_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="club-url-link"
                    >
                      {club.website_url}
                    </a>
                  </div>
                )}

                <div className="club-description">
                  {club.website_description || club.description || 'No description available'}
                </div>
                
                <div className="club-details">
                  {(club.location || club.county) && (
                    <span className="club-detail">📍 {[club.location, club.county].filter(Boolean).join(', ')}</span>
                  )}
                  {club.venue && (
                    <span className="club-detail">🏢 {club.venue}</span>
                  )}
                  {club.cost !== undefined && club.cost !== null && (
                    <span className="club-detail">
                      💰 {(typeof club.cost === 'number' ? club.cost : parseFloat(club.cost as any)) > 0 ? `£${(typeof club.cost === 'number' ? club.cost : parseFloat(club.cost as any)).toFixed(2)}` : 'not specified'}
                    </span>
                  )}
                  {club.usage_count && club.usage_count > 0 && (
                    <span className="club-detail">
                      👥 {club.usage_count} {club.usage_count === 1 ? 'booking' : 'bookings'} (6 months)
                    </span>
                  )}
                  {club.min_child_age && club.max_child_age && (
                    <span className="club-detail">
                      🎂 Booked by children ages {club.min_child_age === club.max_child_age ? club.min_child_age : `${club.min_child_age}-${club.max_child_age}`}
                    </span>
                  )}
                  {club.activity_type && (
                    <span className="club-detail">
                      🎯 {club.activity_type}
                    </span>
                  )}
                  {club.contact_phone && (
                    <span className="club-detail">
                      📞 {club.contact_phone}
                    </span>
                  )}
                  {club.contact_email && (
                    <span className="club-detail">
                      ✉️ {club.contact_email}
                    </span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="club-actions-bottom">
                  {/* Edit button for clubs owned by current user */}
                  {currentUserUuid && club.parent_uuid === currentUserUuid && (
                    <button
                      className="edit-club-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingClub(club);
                        setShowAddClubModal(true);
                      }}
                    >
                      Edit
                    </button>
                  )}

                  {/* Select button in selection mode */}
                  {(propIsSelectionMode || isSelectionMode) && (
                    <button
                      className="select-club-btn"
                      onClick={() => handleClubSelection(club)}
                    >
                      Select Club
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Club Modal */}
      {showAddClubModal && (
        <AddClubModal
          onSave={handleSaveNewClub}
          onCancel={() => {
            setShowAddClubModal(false);
            setEditingClub(null);
          }}
          editClub={editingClub || undefined}
        />
      )}
    </div>
  );
};

export default ClubsScreen;